"use client";
import * as React from "react";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Button, buttonVariants } from "./button";
import { cn } from "../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { endOfMonth, format, startOfMonth, subDays, subYears } from "date-fns";
import { cva } from "class-variance-authority";

// const addMonths = (input, months) => {
//   const date = new Date(input);
//   date.setDate(1);
//   date.setMonth(date.getMonth() + months);
//   date.setDate(
//     Math.min(
//       input.getDate(),
//       getDaysInMonth(date.getFullYear(), date.getMonth() + 1)
//     )
//   );
//   return date;
// };
// const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

const MONTHS = [
  [
    { number: 0, name: "Jan", yearOffset: 0 },
    { number: 1, name: "Feb", yearOffset: 0 },
    { number: 2, name: "Mar", yearOffset: 0 },
    { number: 3, name: "Apr", yearOffset: 0 },
    { number: 0, name: "Jan", yearOffset: 1 },
    { number: 1, name: "Feb", yearOffset: 1 },
    { number: 2, name: "Mar", yearOffset: 1 },
    { number: 3, name: "Apr", yearOffset: 1 },
  ],
  [
    { number: 4, name: "May", yearOffset: 0 },
    { number: 5, name: "Jun", yearOffset: 0 },
    { number: 6, name: "Jul", yearOffset: 0 },
    { number: 7, name: "Aug", yearOffset: 0 },
    { number: 4, name: "May", yearOffset: 1 },
    { number: 5, name: "Jun", yearOffset: 1 },
    { number: 6, name: "Jul", yearOffset: 1 },
    { number: 7, name: "Aug", yearOffset: 1 },
  ],
  [
    { number: 8, name: "Sep", yearOffset: 0 },
    { number: 9, name: "Oct", yearOffset: 0 },
    { number: 10, name: "Nov", yearOffset: 0 },
    { number: 11, name: "Dec", yearOffset: 0 },
    { number: 8, name: "Sep", yearOffset: 1 },
    { number: 9, name: "Oct", yearOffset: 1 },
    { number: 10, name: "Nov", yearOffset: 1 },
    { number: 11, name: "Dec", yearOffset: 1 },
  ],
];

const detectedCountry = "AU";
const today = new Date();

// 📅 Country-wise Financial Year Configuration
const financialYearConfig = {
  IN: { startMonth: 3, startDay: 1 }, // India: April 1 - March 31
  US: { startMonth: 9, startDay: 1 }, // USA: Oct 1 - Sep 30 (Federal)
  UK: { startMonth: 3, startDay: 6 }, // UK: April 6 - April 5
  AU: { startMonth: 6, startDay: 1 }, // Australia: July 1 - June 30
  CA: { startMonth: 3, startDay: 1 }, // Canada: April 1 - March 31
  JP: { startMonth: 3, startDay: 1 }, // Japan: April 1 - March 31
  CN: { startMonth: 0, startDay: 1 }, // China: Jan 1 - Dec 31
  DE: { startMonth: 0, startDay: 1 }, // Germany: Jan 1 - Dec 31
  FR: { startMonth: 0, startDay: 1 }, // France: Jan 1 - Dec 31
  AE: { startMonth: 0, startDay: 1 }, // UAE: Jan 1 - Dec 31
  ZA: { startMonth: 2, startDay: 1 }, // South Africa: March 1 - Feb 28
  NZ: { startMonth: 3, startDay: 1 }, // New Zealand: April 1 - March 31
};

const getEndOfMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate(); // Gets the last day of the month
};

// 📆 Function to Get Financial Year Range
const getFinancialYearRange = (date, countryCode) => {
  const config = financialYearConfig[countryCode] || financialYearConfig["AU"]; // Default to India
  const { startMonth, startDay } = config;

  const year =
    date.getMonth() >= startMonth ? date.getFullYear() : date.getFullYear() - 1;

  const endMonth = (startMonth + 11) % 12; // Financial year runs for 12 months
  const endYear = startMonth === 0 ? year : year + 1; // Adjust for calendar year cases

  const lastDay = getEndOfMonth(endYear, endMonth); // Get the correct last day of the end month

  return {
    start: new Date(year, startMonth, startDay),
    end: new Date(endYear, endMonth, lastDay),
  };
};

const thisFY = getFinancialYearRange(today, detectedCountry);
const lastFY = getFinancialYearRange(subYears(today, 1), detectedCountry);

// 🌍 Country-wise Financial Year Start Month
const financialYearQuaterConfig = {
  IN: 3, // India: April 1 - March 31
  US: 9, // USA: Oct 1 - Sep 30 (Federal)
  UK: 3, // UK: April 6 - April 5
  AU: 6, // Australia: July 1 - June 30
  CA: 3, // Canada: April 1 - March 31
  JP: 3, // Japan: April 1 - March 31
  CN: 0, // China: Jan 1 - Dec 31
  DE: 0, // Germany: Jan 1 - Dec 31
  FR: 0, // France: Jan 1 - Dec 31
  AE: 0, // UAE: Jan 1 - Dec 31
  ZA: 2, // South Africa: March 1 - Feb 28
  NZ: 3, // New Zealand: April 1 - March 31
};

const getQuarterDetails = (date, startMonth) => {
  // Get the current month and year
  const currentMonth = date.getMonth();
  const currentYear = date.getFullYear();

  // Adjust for financial year
  const financialYearStart =
    currentMonth >= startMonth ? currentYear : currentYear - 1;

  // Calculate the quarter index (0 = Q1, 1 = Q2, 2 = Q3, 3 = Q4)
  const quarterIndex = Math.floor(((currentMonth - startMonth + 12) % 12) / 3);

  // 🔹 Compute Current Quarter Start & End Dates
  const quarterStartMonth = (startMonth + quarterIndex * 3) % 12;
  const quarterStartYear =
    quarterStartMonth >= startMonth
      ? financialYearStart
      : financialYearStart + 1;
  const quarterEndMonth = (quarterStartMonth + 2) % 12;
  const quarterEndYear =
    quarterEndMonth < quarterStartMonth
      ? quarterStartYear + 1
      : quarterStartYear;
  const quarterLastDay = new Date(
    quarterEndYear,
    quarterEndMonth + 1,
    0
  ).getDate();

  const thisQuarter = {
    start: new Date(quarterStartYear, quarterStartMonth, 1),
    end: new Date(quarterEndYear, quarterEndMonth, quarterLastDay),
  };

  // 🔹 Compute Last Quarter
  const lastQuarterEnd = new Date(thisQuarter.start);
  lastQuarterEnd.setDate(lastQuarterEnd.getDate() - 1);
  const lastQuarterStart = new Date(lastQuarterEnd);
  lastQuarterStart.setMonth(lastQuarterStart.getMonth() - 2);
  lastQuarterStart.setDate(1);

  return {
    thisQuarter,
    lastQuarter: { start: lastQuarterStart, end: lastQuarterEnd },
  };
};

// const quaterStartMonth =
//   financialYearQuaterConfig[detectedCountry] ||
//   financialYearQuaterConfig["AU"]; // Default to AU

const quaterStartMonth = financialYearQuaterConfig["AU"]; // Default to AU
const { thisQuarter, lastQuarter } = getQuarterDetails(today, quaterStartMonth);

const QUICK_SELECTORS = [
  //   {
  //     label: "This year",
  //     startMonth: new Date(new Date().getFullYear(), 0),
  //     endMonth: new Date(new Date().getFullYear(), 11),
  //   },
  //   {
  //     label: "Last year",
  //     startMonth: new Date(new Date().getFullYear() - 1, 0),
  //     endMonth: new Date(new Date().getFullYear() - 1, 11),
  //   },
  { label: "Financial Year", startMonth: thisFY.start, endMonth: thisFY.end },
  {
    label: "Last Financial Year",
    startMonth: lastFY.start,
    endMonth: lastFY.end,
  },
  {
    label: "This Month",
    startMonth: startOfMonth(today),
    endMonth: endOfMonth(today),
  },
  {
    label: "Last Month",
    startMonth: startOfMonth(subDays(today, today.getDate())),
    endMonth: endOfMonth(subDays(today, today.getDate())),
  },
  {
    label: "Current Quarter",
    startMonth: thisQuarter.start,
    endMonth: thisQuarter.end,
  },
  {
    label: "Last Quarter",
    startMonth: lastQuarter.start,
    endMonth: lastQuarter.end,
  },
];

const multiSelectVariants = cva(
  "flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium text-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground text-background",
        link: "text-primary underline-offset-4 hover:underline text-background",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const MonthRangePicker = React.forwardRef(
  (
    {
      onMonthRangeSelect,
      onStartMonthSelect,
      callbacks,
      closeOnSelect = true,
      selectedMonthRange,
      onYearBackward,
      onYearForward,
      variant,
      minDate,
      maxDate,
      quickSelectors,
      showQuickSelectors,
      className,
      align,
      ...props
    },
    ref
  ) => {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const handleClose = () => setIsPopoverOpen(false);

    const handleTogglePopover = () => setIsPopoverOpen((prev) => !prev);
    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            {...props}
            className={cn(
              "w-auto",
              multiSelectVariants({ variant, className })
            )}
            ref={ref}
            onClick={handleTogglePopover}
            suppressHydrationWarning
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {selectedMonthRange?.start && selectedMonthRange?.end ? (
              format(selectedMonthRange.start, "MMM yyyy") ===
              format(selectedMonthRange.end, "MMM yyyy") ? (
                <>{format(selectedMonthRange.start, "MMM yyyy")}</>
              ) : (
                <>
                  {format(selectedMonthRange.start, "MMM yyyy")} -{" "}
                  {format(selectedMonthRange.end, "MMM yyyy")}
                </>
              )
            ) : (
              <>Select month range</>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto"
          align={align}
          avoidCollisions={false}
          onInteractOutside={handleClose}
          onEscapeKeyDown={handleClose}
          style={{
            maxHeight: "var(--radix-popover-content-available-height)",
            overflowY: "auto",
          }}
        >
          <div className={cn("min-w-[80vw] md:!min-w-[400px]", className)} {...props}>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0">
              <div className="w-full">
                <MonthRangeCal
                  onMonthRangeSelect={onMonthRangeSelect}
                  onStartMonthSelect={onStartMonthSelect}
                  callbacks={callbacks}
                  selectedMonthRange={selectedMonthRange}
                  onYearBackward={onYearBackward}
                  onYearForward={onYearForward}
                  variant={variant}
                  minDate={minDate}
                  maxDate={maxDate}
                  quickSelectors={quickSelectors}
                  showQuickSelectors={showQuickSelectors}
                  setIsPopoverOpen={setIsPopoverOpen}
                  closeOnSelect={closeOnSelect}
                ></MonthRangeCal>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

function MonthRangeCal({
  selectedMonthRange,
  onMonthRangeSelect,
  onStartMonthSelect,
  callbacks,
  variant,
  minDate,
  maxDate,
  quickSelectors = QUICK_SELECTORS,
  showQuickSelectors = true,
  onYearBackward,
  onYearForward,
  setIsPopoverOpen,
  closeOnSelect,
}) {
  const [startYear, setStartYear] = React.useState(
    selectedMonthRange?.start.getFullYear() ?? new Date().getFullYear()
  );
  const [startMonth, setStartMonth] = React.useState(
    selectedMonthRange?.start?.getMonth() ?? new Date().getMonth()
  );
  const [endYear, setEndYear] = React.useState(
    selectedMonthRange?.end?.getFullYear() ?? new Date().getFullYear() + 1
  );
  const [endMonth, setEndMonth] = React.useState(
    selectedMonthRange?.end?.getMonth() ?? new Date().getMonth()
  );
  const [rangePending, setRangePending] = React.useState(false);
  const [endLocked, setEndLocked] = React.useState(true);
  const [menuYear, setMenuYear] = React.useState(startYear);
  const [selector, setSelector] = React.useState();

  if (minDate && maxDate && minDate > maxDate) minDate = maxDate;

  return (
    <div className="flex">
      {showQuickSelectors ? (
        <div className="hidden md:flex flex-col gap-1 pr-4 text-left border-r border-foreground/10">
          {quickSelectors.map((s) => {
            return (
              <Button
                onClick={() => {
                  setStartYear(s.startMonth.getFullYear());
                  setStartMonth(s.startMonth.getMonth());
                  setEndYear(s.endMonth.getFullYear());
                  setEndMonth(s.endMonth.getMonth());
                  setRangePending(false);
                  setEndLocked(true);
                  setSelector(s.label);
                  if (onMonthRangeSelect)
                    onMonthRangeSelect({
                      start: s.startMonth,
                      end: s.endMonth,
                    });
                  closeOnSelect && setIsPopoverOpen(false);

                  if (s.onClick) s.onClick(s);
                }}
                key={s.label}
                variant={s.variant ?? "ghost"}
                className={cn(
                  "justify-start hover:bg-primary/90 hover:text-background",
                  selector === s.label &&
                    "bg-primary text-background hover:bg-primary/90 hover:text-background"
                )}
              >
                {s.label}
              </Button>
            );
          })}
        </div>
      ) : null}
      <div className="md:min-w-[400px] min-w-[80vw] space-y-4 ml-3">
        <div className="flex justify-evenly pt-1 relative items-center">
          <div className="text-sm font-medium">
            {callbacks?.yearLabel ? callbacks?.yearLabel(menuYear) : menuYear}
          </div>
          <div className="space-x-1 flex items-center">
            <button
              onClick={() => {
                setMenuYear(menuYear - 1);
                if (onYearBackward) onYearBackward();
              }}
              className={cn(
                buttonVariants({ variant: variant?.chevrons ?? "outline" }),
                "inline-flex items-center justify-center h-7 w-7 p-0 absolute left-1"
              )}
            >
              <ChevronLeft className="opacity-50 h-4 w-4" />
            </button>
            <button
              onClick={() => {
                setMenuYear(menuYear + 1);
                if (onYearForward) onYearForward();
              }}
              className={cn(
                buttonVariants({ variant: variant?.chevrons ?? "outline" }),
                "inline-flex items-center justify-center h-7 w-7 p-0 absolute right-1"
              )}
            >
              <ChevronRight className="opacity-50 h-4 w-4" />
            </button>
          </div>
          <div className="text-sm font-medium">
            {callbacks?.yearLabel
              ? callbacks?.yearLabel(menuYear + 1)
              : menuYear + 1}
          </div>
        </div>
        <table className="w-full border-collapse space-y-1">
          <tbody>
            {MONTHS.map((monthRow, a) => {
              return (
                <tr key={"row-" + a} className="flex w-full mt-2">
                  {monthRow.map((m, i) => {
                    return (
                      <td
                        key={m.number + "-" + m.yearOffset}
                        className={cn(
                          cn(
                            cn(
                              cn(
                                "h-10 w-1/4 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                                (menuYear + m.yearOffset > startYear ||
                                  (menuYear + m.yearOffset === startYear &&
                                    m.number > startMonth)) &&
                                  (menuYear + m.yearOffset < endYear ||
                                    (menuYear + m.yearOffset === endYear &&
                                      m.number < endMonth)) &&
                                  (rangePending || endLocked)
                                  ? "text-accent-foreground bg-accent"
                                  : ""
                              ),
                              menuYear + m.yearOffset === startYear &&
                                m.number === startMonth &&
                                (rangePending || endLocked)
                                ? "text-accent-foreground bg-accent rounded-l-md"
                                : ""
                            ),
                            menuYear + m.yearOffset === endYear &&
                              m.number === endMonth &&
                              (rangePending || endLocked) &&
                              menuYear + m.yearOffset >= startYear &&
                              m.number >= startMonth
                              ? "text-accent-foreground bg-accent rounded-r-md"
                              : ""
                          ),
                          i === 3 ? "mr-2" : i === 4 ? "ml-2" : ""
                        )}
                        onMouseEnter={() => {
                          if (rangePending && !endLocked) {
                            setEndYear(menuYear + m.yearOffset);
                            setEndMonth(m.number);
                          }
                        }}
                      >
                        <button
                          onClick={() => {
                            if (rangePending) {
                              if (
                                menuYear + m.yearOffset < startYear ||
                                (menuYear + m.yearOffset === startYear &&
                                  m.number < startMonth)
                              ) {
                                setRangePending(true);
                                setEndLocked(false);
                                setStartMonth(m.number);
                                setStartYear(menuYear + m.yearOffset);
                                setEndYear(menuYear + m.yearOffset);
                                setEndMonth(m.number);
                                if (onStartMonthSelect)
                                  onStartMonthSelect(
                                    new Date(menuYear + m.yearOffset, m.number)
                                  );
                              } else {
                                setRangePending(false);
                                setEndLocked(true);
                                // Event fire data selected

                                if (onMonthRangeSelect)
                                  onMonthRangeSelect({
                                    start: new Date(startYear, startMonth),
                                    end: new Date(
                                      menuYear + m.yearOffset,
                                      m.number
                                    ),
                                  });
                                closeOnSelect && setIsPopoverOpen(false);
                              }
                            } else {
                              setRangePending(true);
                              setEndLocked(false);
                              setStartMonth(m.number);
                              setStartYear(menuYear + m.yearOffset);
                              setEndYear(menuYear + m.yearOffset);
                              setEndMonth(m.number);
                              if (onStartMonthSelect)
                                onStartMonthSelect(
                                  new Date(menuYear + m.yearOffset, m.number)
                                );
                            }
                          }}
                          disabled={
                            (maxDate
                              ? menuYear + m.yearOffset >
                                  maxDate?.getFullYear() ||
                                (menuYear + m.yearOffset ===
                                  maxDate?.getFullYear() &&
                                  m.number > maxDate.getMonth())
                              : false) ||
                            (minDate
                              ? menuYear + m.yearOffset <
                                  minDate?.getFullYear() ||
                                (menuYear + m.yearOffset ===
                                  minDate?.getFullYear() &&
                                  m.number < minDate.getMonth())
                              : false)
                          }
                          className={cn(
                            buttonVariants({
                              variant:
                                (startMonth === m.number &&
                                  menuYear + m.yearOffset === startYear) ||
                                (endMonth === m.number &&
                                  menuYear + m.yearOffset === endYear &&
                                  !rangePending)
                                  ? variant?.calendar?.selected ?? "default"
                                  : variant?.calendar?.main ?? "ghost",
                            }),
                            "h-full w-full p-0 font-normal aria-selected:opacity-100"
                          )}
                        >
                          {callbacks?.monthLabel
                            ? callbacks.monthLabel(m)
                            : m.name}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

MonthRangePicker.displayName = "MonthRangePicker";

export { MonthRangePicker };
